/**
 * 정오 표기 — 줄 단위 상태 전이(T1~T18)·저장 가능 조건·자기 충돌 판정·문자열(순수, React 없음).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 화면 규칙이 아니라 도메인 규칙이다(같은 caseId 번짐·revision·자기 충돌 — DDD). 18행 전이 표를
 * 그대로 테스트 표로 고정한다. reducer는 mode·draft·override만 다룬다 — 캐시·Toast·네트워크는
 * 페이지 몫(결정 G1). 문자열은 spec 원문 그대로다(§6.2 3단계 주의).
 */
import type {
  DoseLabel,
  DoseLabelVerdict,
  DoseLabelSeverity,
  PutDoseLabelBody,
} from "services/api/doseReviewLogs/types";

/* ── 문자열(spec 원문) ─────────────────────────────────────────── */

/** verdict 버튼 라벨(개입 필요·불필요·판단 불가). */
export const VERDICT_LABELS: Record<DoseLabelVerdict, string> = {
  intervene: "개입 필요",
  no_intervene: "불필요",
  undecidable: "판단 불가",
};

/** 등급 select 라벨 = 정답셋 어휘(기계 등급 "1회 과량·부족"과 다름, D-7). 순서 고정. */
export const VERDICT_SEVERITY_OPTIONS: Array<{ value: DoseLabelSeverity; label: string }> = [
  { value: "critical", label: "위험" },
  { value: "caution", label: "초과" },
  { value: "warning", label: "미달" },
  { value: "info", label: "참고" },
];

export const SCOPE_NOTE_TEXT = "이 표기는 이 약의 용량 판정에 대한 것입니다(성분 중복 제외)";
export const SAVED_VALUE_NONE = "저장된 값 없음";
export const NO_CODE_TEXT = "코드가 없어 정답셋에 넣을 수 없는 줄";
export const OUT_OF_RANGE_TEXT = "입력값이 정답셋 범위를 벗어나 넣을 수 없는 줄";

export const RULE_NOTE_APPENDED_TOAST = "룰 검수 메모에도 남겼습니다";
export const SAVE_FAILED_TOAST = "저장하지 못했습니다";
export const CONFLICT_TOAST = "다른 사용자가 방금 수정했습니다";
export const ALREADY_SAVED_TOAST = "이미 저장됨";
export const UNLABEL_FAILED_TOAST = "취소하지 못했습니다";

export const DISCARD_LABEL = "버리고 이동";
export const STAY_LABEL = "머물기";

/** 룰 메모 안내(D-39) — noteTarget 있고 초안 no_intervene일 때만. */
export const ruleNoteNoticeText = (ruleId: string): string =>
  `이 메모는 룰 ${ruleId}의 검수 메모에도 남습니다(표기를 고치거나 취소해도 지워지지 않습니다)`;

/** 같은 입력 번짐 규모(D-33) — N≥2일 때만 보인다. */
export const sameCaseText = (lineCount: number, prescriptionCount: number): string =>
  `같은 입력 ${lineCount}줄(${prescriptionCount}처방)에 함께 적용`;

/** 같은 입력 툴팁(D-33 — 기간은 apiRangeToPeriod로 YYYY-MM-DD, §3.10 ⑫). */
export const sameCaseTooltip = (start: string, end: string): string =>
  `${start}~${end} 판정 기록 안에서 센 수입니다. 이 기간 밖 기록·정답셋 표본의 같은 입력에도 이 표기가 적용됩니다`;

/** 이동 배너 문구(D-19). 버튼 라벨은 DISCARD_LABEL·STAY_LABEL. */
export const unsavedBannerText = (editingCountN: number): string =>
  `저장 안 한 표기 ${editingCountN}줄`;

export const notLabelableText = (reason: "no_code" | "out_of_range"): string =>
  reason === "no_code" ? NO_CODE_TEXT : OUT_OF_RANGE_TEXT;

export const severityLabel = (s: DoseLabelSeverity): string =>
  VERDICT_SEVERITY_OPTIONS.find((o) => o.value === s)?.label ?? s;

/** +09:00 문자열 → "YYYY-MM-DD HH:mm"(슬라이스 — reviewedAt은 이미 +09:00, D1). */
export const formatLabelMinute = (iso: string): string => `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;

/** 보기 표기 한 줄(§3.10 ⑥) — verdict[ · 등급] · 표기자 · 시각(분). 메모는 별도 줄. */
export const labelViewMain = (label: DoseLabel): string => {
  const who = label.reviewedByName ?? label.reviewedBy;
  const sev = label.verdictSeverity ? ` · ${severityLabel(label.verdictSeverity)}` : "";
  return `${VERDICT_LABELS[label.verdict]}${sev} · ${who} · ${formatLabelMinute(label.reviewedAt)}`;
};

/** 작성 중 "저장된 값" 병기(D-36) — 날짜만(YYYY-MM-DD). 표기 없으면 "저장된 값 없음". */
export const savedValueText = (label: DoseLabel | null): string => {
  if (!label) return SAVED_VALUE_NONE;
  const who = label.reviewedByName ?? label.reviewedBy;
  return `저장된 값: ${VERDICT_LABELS[label.verdict]}(${who}·${label.reviewedAt.slice(0, 10)})`;
};

/* ── 줄 상태 모델(결정 A1) ─────────────────────────────────────── */

export type LabelMode = "view" | "editing" | "saving" | "cancelling";

export interface LabelDraft {
  verdict: DoseLabelVerdict;
  verdictSeverity: DoseLabelSeverity | null;
  verdictNote: string;
}

/** override = T7·T10 그 줄 전용 보유 표기(같은 caseId 성공 이벤트에서 해제). */
export interface LineState {
  mode: LabelMode;
  draft: LabelDraft | null;
  override?: { label: DoseLabel | null };
}

export type LabelEvent =
  | { type: "verdict"; index: number; verdict: DoseLabelVerdict; held: DoseLabel | null } // T1·T3
  | { type: "severity"; index: number; value: DoseLabelSeverity | null } // T2
  | { type: "note"; index: number; value: string } // T2
  | { type: "saveStart" | "saveFail" | "draftCancel" | "unlabelStart" | "unlabelFail"; index: number } // T4·T6·T16·T8·T11
  | { type: "saveConflict" | "unlabelConflict"; index: number; label: DoseLabel | null } // T7·T10
  | { type: "settled"; index: number; sameCaseIndexes: number[] } // T5·T9·T18
  | { type: "discardAll" }; // T13

/* ── 순수 규칙 ─────────────────────────────────────────────────── */

/** [저장] 활성 조건(T2). */
export const canSave = (draft: LabelDraft): boolean => {
  if (draft.verdict === "intervene") return draft.verdictSeverity != null;
  if (draft.verdict === "undecidable") return draft.verdictNote.trim() !== "";
  return true; // no_intervene
};

/** verdict 선택(T1·T3) — 보기에서 시작하면 held 복사, intervene이 아니면 등급 null·메모 보존. */
export const applyVerdict = (
  draft: LabelDraft | null,
  held: DoseLabel | null,
  verdict: DoseLabelVerdict
): LabelDraft => {
  const base: LabelDraft = draft ?? {
    verdict,
    verdictSeverity: held?.verdictSeverity ?? null,
    verdictNote: held?.verdictNote ?? "",
  };
  const next: LabelDraft = { ...base, verdict };
  if (verdict !== "intervene") next.verdictSeverity = null;
  return next;
};

/** PUT 본문(§3.10 ⑩) — 비intervene이면 등급 null, 메모는 trim 뒤 빈 값이면 null. */
export const toPutBody = (draft: LabelDraft, expectedRevision: number): PutDoseLabelBody => ({
  verdict: draft.verdict,
  verdictSeverity: draft.verdict === "intervene" ? draft.verdictSeverity : null,
  verdictNote: draft.verdictNote.trim() || null,
  expectedRevision,
});

const normNote = (s: string | null | undefined): string | null => (s ?? "").trim() || null;

/** 자기 충돌(T18·D-9) — 현재 표기가 로그인 사용자 것이고 verdict·등급·메모가 보낸 초안과 같음. */
export const isSelfConflict = (
  sent: LabelDraft,
  label: DoseLabel | null,
  userId: string | null | undefined
): boolean => {
  if (!label || !userId) return false;
  if (label.reviewedBy !== userId) return false;
  if (label.verdict !== sent.verdict) return false;
  const sentSeverity = sent.verdict === "intervene" ? sent.verdictSeverity : null;
  if ((label.verdictSeverity ?? null) !== (sentSeverity ?? null)) return false;
  return normNote(label.verdictNote) === normNote(sent.verdictNote);
};

/** 6122 응답 data의 label 추출(§3.10 ⑪) — 형태가 깨지면 null. */
export const conflictLabel = (data: unknown): DoseLabel | null => {
  if (!data || typeof data !== "object") return null;
  const label = (data as { label?: unknown }).label;
  if (!label || typeof label !== "object") return null;
  const l = label as Record<string, unknown>;
  if (
    typeof l.verdict !== "string" ||
    typeof l.revision !== "number" ||
    typeof l.reviewedBy !== "string" ||
    typeof l.reviewedAt !== "string" // slice 크래시 방어 — reviewedAt 없으면 표기 없음으로 본다
  ) {
    return null;
  }
  return label as DoseLabel;
};

/** 작성 중 줄 수(T12의 N) — 저장 중·취소 중은 세지 않는다(§3.10 ⑤). */
export const editingCount = (s: Record<number, LineState>): number =>
  Object.values(s).filter((l) => l.mode === "editing").length;

/** 정오 상태 한 줄(표·접힌 카드 헤더) — 작성 중 > 보유 표기 verdict > 미표기(상세 화면 개편). */
export const labelStateText = (held: DoseLabel | null, mode?: LabelMode): string => {
  if (mode === "editing") return "작성 중";
  if (held) return VERDICT_LABELS[held.verdict];
  return "미표기";
};

/** 줄 상태 전이(T1~T18의 mode·draft·override 부분만). 출발 상태와 안 맞는 이벤트는 무시(방어). */
export const labelLineReducer = (
  s: Record<number, LineState>,
  e: LabelEvent
): Record<number, LineState> => {
  const cur = (i: number): LineState => s[i] ?? { mode: "view", draft: null };
  switch (e.type) {
    case "verdict": {
      const c = cur(e.index);
      if (c.mode !== "view" && c.mode !== "editing") return s;
      return { ...s, [e.index]: { ...c, mode: "editing", draft: applyVerdict(c.draft, e.held, e.verdict) } };
    }
    case "severity": {
      const c = cur(e.index);
      if (c.mode !== "editing" || !c.draft) return s;
      return { ...s, [e.index]: { ...c, draft: { ...c.draft, verdictSeverity: e.value } } };
    }
    case "note": {
      const c = cur(e.index);
      if (c.mode !== "editing" || !c.draft) return s;
      return { ...s, [e.index]: { ...c, draft: { ...c.draft, verdictNote: e.value } } };
    }
    case "saveStart": {
      const c = cur(e.index);
      if (c.mode !== "editing") return s;
      return { ...s, [e.index]: { ...c, mode: "saving" } };
    }
    case "saveFail": {
      const c = cur(e.index);
      if (c.mode !== "saving") return s;
      return { ...s, [e.index]: { ...c, mode: "editing" } };
    }
    case "draftCancel": {
      const c = cur(e.index);
      if (c.mode !== "editing") return s;
      return { ...s, [e.index]: { ...c, mode: "view", draft: null } };
    }
    case "unlabelStart": {
      const c = cur(e.index);
      if (c.mode !== "view") return s;
      return { ...s, [e.index]: { ...c, mode: "cancelling" } };
    }
    case "unlabelFail": {
      const c = cur(e.index);
      if (c.mode !== "cancelling") return s;
      return { ...s, [e.index]: { ...c, mode: "view" } };
    }
    case "saveConflict": {
      const c = cur(e.index);
      if (c.mode !== "saving") return s;
      return { ...s, [e.index]: { ...c, mode: "editing", override: { label: e.label } } };
    }
    case "unlabelConflict": {
      const c = cur(e.index);
      if (c.mode !== "cancelling") return s;
      return { ...s, [e.index]: { mode: "view", draft: null, override: { label: e.label } } };
    }
    case "settled": {
      // 그 줄: 보기·초안 폐기·override 제거(held는 갱신된 캐시에서 온다). 같은 caseId 줄: override만 해제(T17 — 초안 유지).
      const next = { ...s, [e.index]: { mode: "view", draft: null } as LineState };
      for (const i of e.sameCaseIndexes) {
        if (i === e.index) continue;
        const c = next[i];
        if (c && c.override) next[i] = { mode: c.mode, draft: c.draft };
      }
      return next;
    }
    case "discardAll": {
      const next: Record<number, LineState> = {};
      for (const k of Object.keys(s)) {
        next[Number(k)] = { ...s[Number(k)], mode: "view", draft: null };
      }
      return next;
    }
    default:
      return s;
  }
};
